// src/components/frontend/companies/Table.js
import React from "react";
import {
    Paper,
    Grid,
    Typography,
    Box,
    Button,
    Chip,
    Stack
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { useI18n } from "../../../i18n/I18nContext";

import PlaceIcon from "@mui/icons-material/Place";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { cropImageStyle } from "../../backend/companies/utils/companyUtils";
import { ClassificationIcon } from "../../utils/classificationIcons";
import { AwardBadge } from "../../utils/awardIcons";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const resolveImg = (pathOrUrl) => {
    if (!pathOrUrl) return null;
    const s = String(pathOrUrl).trim();
    if (!s) return null;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    const base = String(API_BASE || "").replace(/\/$/, "");
    const p = s.startsWith("/") ? s : `/${s}`;
    return `${base}${p}`;
};

const ContactRow = ({ icon, children }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Box sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: "#fdf5e8",
            border: "1px solid var(--domain-primary-light)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
        }}>
            {React.cloneElement(icon, { sx: { fontSize: 14, color: "var(--domain-primary-dark)" } })}
        </Box>
        {children}
    </Box>
);

function formatCategory(item) {
    if (!item) return "";
    if (typeof item === "string") return item;
    return item.label_de || item.name || "";
}

function formatClassification(item) {
    if (!item) return "";
    const label = item.label_de || item.name || "";
    const value = item.value;
    const hasValue = value !== undefined && value !== null && String(value).trim() !== "";
    if (item.value_type === "boolean") return label;
    if (!hasValue) return label;
    return `${value} ${label}${item.is_superior ? " Superior" : ""}`.trim();
}

export default function HotelsTable({ rows }) {
    const navigate = useNavigate();
    const { t } = useI18n();

    const getButtonLabel = (row) => {
        const category = (row.category || row.category_name || "default")
            .toLowerCase()
            .replace(/\s+/g, "");
        const key = `table.buttons.view.${category}`;
        const translation = t(key);
        if (translation === key) return t("table.buttons.view.default");
        return translation;
    };

    const normalizeUrl = (url) => {
    if (!url) return "";
    if (!/^https?:\/\//i.test(url)) {
        return "https://" + url;
    }
    return url;
};

    if (!rows || rows.length === 0) {
        return (
            <Box sx={{
                textAlign: "center",
                py: 10,
                px: 4,
                borderRadius: 4,
                border: "1.5px dashed #e0d9ce",
                background: "linear-gradient(135deg, #fdfaf6 0%, #f5f0e8 100%)",
            }}>
                <Box sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, var(--domain-primary-light), var(--domain-primary))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                    boxShadow: "0 4px 16px rgba(240,172,77,0.3)"
                }}>
                    <PlaceIcon sx={{ color: "#fff", fontSize: 24 }} />
                </Box>
                <Typography sx={{ color: "#9a8e80", fontSize: "1rem" }}>
                    {t("table.noResults")}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {rows.map((row) => {
                const imgUrl = resolveImg(row.start_image_url);
                const address = [
                    row.street,
                    [row.zip, row.city].filter(Boolean).join(" ")
                ].filter(Boolean).join(", ");

                const categories = Array.isArray(row.categories)
                    ? row.categories
                    : row.categories
                        ? row.categories.split("||")
                        : [];
                const primaryClassification = formatClassification(row.primary_classification);
                const awards = Array.isArray(row.awards) ? row.awards : [];
                const detailPath = `/companies/${row.slug || row.id}`;

                return (
                    <Paper
                        key={row.id}
                        elevation={0}
                        sx={{
                            border: "1px solid #e8e0d4",
                            borderRadius: 4,
                            overflow: "hidden",
                            backgroundColor: "background.paper",
                            transition: "box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease",
                            "&:hover": {
                                boxShadow: "0 12px 40px rgba(26,37,56,0.10), 0 2px 8px rgba(240,172,77,0.08)",
                                transform: "translateY(-2px)",
                                borderColor: "var(--domain-primary-light)",
                            },
                        }}
                    >
                        <Grid container sx={{ minHeight: 220, height: { md: 220 } }}>

                            {/* IMAGE */}
                            <Grid item xs={12} md={3}>
                                <Box
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`${row.name} ansehen`}
                                    onClick={() => navigate(detailPath)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            navigate(detailPath);
                                        }
                                    }}
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        height: 220,
                                        overflow: "hidden",
                                        backgroundColor: "#ede6da",
                                        cursor: "pointer",
                                        outline: "none",
                                        "&:focus-visible": {
                                            boxShadow: "inset 0 0 0 3px var(--domain-primary)",
                                        },
                                        "&:hover img": {
                                            transform: "scale(1.04)",
                                        },
                                    }}
                                >
                                    {imgUrl ? (
                                        <>
                                            <img
                                                src={imgUrl}
                                                alt={row.name}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    ...cropImageStyle(row.start_image_crop),
                                                    display: "block",
                                                    transition: "transform 0.4s ease",
                                                }}
                                            />
                                            {/* subtle gradient overlay bottom */}
                                            <Box sx={{
                                                position: "absolute",
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                height: "40%",
                                                background: "linear-gradient(to top, rgba(26,37,56,0.18), transparent)",
                                                pointerEvents: "none",
                                            }} />
                                        </>
                                    ) : (
                                        <Box sx={{
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "linear-gradient(145deg, #ede6da 0%, #d8cfc2 100%)",
                                        }}>
                                            <Box sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: "50%",
                                                background: "linear-gradient(135deg, var(--domain-primary-light), var(--domain-primary))",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0 4px 20px rgba(240,172,77,0.35)"
                                            }}>
                                                <Typography sx={{
                                                    fontSize: "1.8rem",
                                                    fontWeight: 700,
                                                    color: "#fff",
                                                    lineHeight: 1,
                                                }}>
                                                    {(row.name || "?")[0].toUpperCase()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            {/* RIGHT SIDE — 3 columns: info | contact | button */}
                            <Grid item xs={12} md={9}>
                                <Grid container sx={{ height: "100%" }}>

                                    {/* COL 1: Name + Address + Tags */}
                                    <Grid item xs={12} md={5} sx={{
                                        p: { xs: 2.5, md: 3 },
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        gap: 1,
                                        borderRight: { md: "1px solid #ede6da" },
                                    }}>
                                        <Typography sx={{
                                            fontSize: "1.25rem",
                                            fontWeight: 600,
                                            color: "var(--domain-text)",
                                            lineHeight: 1.25,
                                            letterSpacing: "-0.01em",
                                        }}>
                                            {row.name}
                                        </Typography>

                                        {address && (
                                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                                                <PlaceIcon sx={{ fontSize: 15, color: "var(--domain-primary)", mt: "2px", flexShrink: 0 }} />
                                                <Typography sx={{ color: "#9a8e80", fontSize: "0.85rem", lineHeight: 1.4 }}>
                                                    {address}
                                                </Typography>
                                            </Box>
                                        )}

                                        {(categories.length > 0 || primaryClassification || awards.length > 0) && (
                                            <Stack
                                                direction="row"
                                                spacing={0.75}
                                                flexWrap="wrap"
                                                useFlexGap
                                                sx={{ pt: 0.25, alignItems: "center" }}
                                            >
                                                {categories.map((cat, i) => (
                                                    <Chip
                                                        key={`cat-${i}`}
                                                        label={formatCategory(cat)}
                                                        size="small"
                                                        sx={{ height: 24 }}
                                                    />
                                                ))}

                                                {primaryClassification && (
                                                    <Chip
                                                        label={primaryClassification}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        icon={<ClassificationIcon iconName={row.primary_classification?.icon_name} />}
                                                        sx={{ height: 24 }}
                                                    />
                                                )}

                                                {awards.slice(0, 4).map((award) => (
                                                    <AwardBadge key={award.id} award={award} size={20} />
                                                ))}
                                            </Stack>
                                        )}
                                    </Grid>

                                    {/* COL 2: Contact info */}
                                    <Grid item xs={12} md={4} sx={{
                                        p: { xs: 2.5, md: 3 },
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        gap: 0.75,
                                        borderRight: { md: "1px solid #ede6da" },
                                        borderTop: { xs: "1px solid #ede6da", md: "none" },
                                    }}>
                                        {row.phone1 && (
                                            <ContactRow icon={<PhoneIcon />}>
                                                <Typography sx={{ fontSize: "0.875rem", color: "#3d3530" }}>
                                                    {row.phone1}
                                                </Typography>
                                            </ContactRow>
                                        )}
                                        {row.email && (
                                            <ContactRow icon={<EmailIcon />}>
                                                <Typography
                                                    component="a"
                                                    href={`mailto:${row.email}`}
                                                    sx={{
                                                        fontSize: "0.875rem",
                                                        color: "var(--domain-primary-dark)",
                                                        textDecoration: "none",
                                                        wordBreak: "break-all",
                                                        "&:hover": { textDecoration: "underline" }
                                                    }}
                                                >
                                                    {row.email}
                                                </Typography>
                                            </ContactRow>
                                        )}
                                        {(row.homepage || row.facebook || row.instagram) && (
                                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                                                {row.homepage && (
                                                    <Button
                                                        component="a"
                                                        href={normalizeUrl(row.homepage)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<LanguageIcon />}
                                                        sx={{ minWidth: 0, textTransform: "none", borderColor: "var(--domain-primary-light)", color: "var(--domain-primary-dark)" }}
                                                    >
                                                        Website
                                                    </Button>
                                                )}
                                                {row.facebook && (
                                                    <Button
                                                        component="a"
                                                        href={row.facebook}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<FacebookIcon />}
                                                        sx={{ minWidth: 0, textTransform: "none", borderColor: "var(--domain-primary-light)", color: "var(--domain-primary-dark)" }}
                                                    >
                                                        Facebook
                                                    </Button>
                                                )}
                                                {row.instagram && (
                                                    <Button
                                                        component="a"
                                                        href={row.instagram}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<InstagramIcon />}
                                                        sx={{ minWidth: 0, textTransform: "none", borderColor: "var(--domain-primary-light)", color: "var(--domain-primary-dark)" }}
                                                    >
                                                        Instagram
                                                    </Button>
                                                )}
                                            </Stack>
                                        )}
                                    </Grid>

                                    {/* COL 3: Button */}
                                    <Grid item xs={12} md={3} sx={{
                                        p: { xs: 2.5, md: 2.5 },
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderTop: { xs: "1px solid #ede6da", md: "none" },
                                    }}>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            fullWidth
                                            endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important", transition: "transform 0.2s ease" }} />}
                                            onClick={() => navigate(detailPath)}
                                            sx={{
                                                background: "linear-gradient(135deg, var(--domain-primary-light) 0%, var(--domain-primary) 100%)",
                                                color: "var(--domain-primary-contrast)",
                                                fontWeight: 700,
                                                fontSize: "0.875rem",
                                                textTransform: "none",
                                                px: 2,
                                                py: 1.25,
                                                borderRadius: 2.5,
                                                letterSpacing: "0.01em",
                                                boxShadow: "0 2px 12px rgba(240,172,77,0.30)",
                                                transition: "all 0.2s ease",
                                                whiteSpace: "nowrap",
                                                "&:hover": {
                                                    background: "linear-gradient(135deg, var(--domain-text) 0%, var(--domain-text) 100%)",
                                                    boxShadow: "0 4px 20px rgba(26,37,56,0.25)",
                                                    "& .MuiButton-endIcon": {
                                                        transform: "translateX(4px)"
                                                    }
                                                },
                                            }}
                                        >
                                            {getButtonLabel(row)}
                                        </Button>
                                    </Grid>

                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                );
            })}
        </Box>
    );
}
